import React, { useEffect, useState } from 'react';
import { protectedFileUrl, fetchProtectedBlob } from './protectedMedia';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { X, ExternalLink, Download, FileText, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const absolute = (url) => (url?.startsWith('/api') ? `${API}${url}` : url);

/** Right-hand slide-over preview for page attachments (PDF, image, video, other). */
export const AttachmentPreview = ({ attachments = [], index, onClose, onNavigate }) => {
  const attachment = index !== null && index >= 0 ? attachments[index] : null;
  const [blob, setBlob] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true; let objectUrl; setBlob(''); setError('');
    if (attachment && protectedFileUrl(attachment.url)) fetchProtectedBlob(attachment.url).then(url => { objectUrl = url; if (live) setBlob(url); else URL.revokeObjectURL(url); }).catch(e => { if (live) setError(e.message); });
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [attachment?.url]);

  useEffect(() => {
    if (!attachment) return;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight' && index < attachments.length - 1) onNavigate(index + 1);
      if (event.key === 'ArrowLeft' && index > 0) onNavigate(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attachment, index, attachments.length, onClose, onNavigate]);

  if (!attachment) return null;

  const url = protectedFileUrl(attachment.url) ? blob : absolute(attachment.url);
  const type = attachment.content_type || '';
  const isImage = attachment.is_image || type.startsWith('image/');
  const isPdf = attachment.is_pdf || type === 'application/pdf';
  const isVideo = type.startsWith('video/');

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-slate-900/30 backdrop-blur-[2px] z-[205] animate-in fade-in duration-150"
        onClick={onClose}
        data-testid="kb-preview-overlay"
      />
      <aside
        className="fixed top-0 right-0 h-full w-full sm:w-[520px] lg:w-[620px] bg-white shadow-2xl border-l border-slate-200 flex flex-col animate-in slide-in-from-right duration-200 z-[210]"
        data-testid="kb-attachment-preview"
      >
        <header className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-200">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate" data-testid="kb-preview-filename">
              {attachment.filename || 'Attachment'}
            </p>
            <p className="text-xs text-slate-400">
              {type || 'file'}{attachment.size ? ` · ${Math.max(1, Math.round(attachment.size / 1024))} KB` : ''}
              {attachments.length > 1 ? ` · ${index + 1} of ${attachments.length}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {attachments.length > 1 && (
              <>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" disabled={index === 0} onClick={() => onNavigate(index - 1)} data-testid="kb-preview-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" disabled={index === attachments.length - 1} onClick={() => onNavigate(index + 1)} data-testid="kb-preview-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
            <a href={url} target="_blank" rel="noreferrer" title="Open in new tab" data-testid="kb-preview-open">
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0"><ExternalLink className="h-4 w-4" /></Button>
            </a>
            <a href={url} download={attachment.filename} title="Download" data-testid="kb-preview-download">
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0"><Download className="h-4 w-4" /></Button>
            </a>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={onClose} data-testid="kb-preview-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-auto bg-slate-100">
          {error && <p data-testid="kb-attachment-error" className="p-4 text-sm text-red-700">{error}</p>}
          {!url && !error && <p data-testid="kb-attachment-loading" className="p-4 text-sm">Loading attachment…</p>}
          {url && isPdf && (
            <div className="h-full flex flex-col">
              <iframe src={`${url}#view=FitH`} title={attachment.filename} className="w-full flex-1 border-0" data-testid="kb-preview-pdf" />
              <p className="text-[11px] text-slate-500 text-center py-1.5 bg-white border-t border-slate-200">
                PDF not rendering? <a href={url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">open it in a new tab</a>
              </p>
            </div>
          )}
          {url && isImage && (
            <div className="p-4 flex items-center justify-center min-h-full">
              <img src={url} alt={attachment.filename} className="max-w-full rounded-lg shadow-sm" data-testid="kb-preview-image" />
            </div>
          )}
          {url && isVideo && (
            <div className="p-4">
              <video src={url} controls className="w-full rounded-lg" data-testid="kb-preview-video" />
            </div>
          )}
          {!isPdf && !isImage && !isVideo && (
            <div className="h-full flex flex-col items-center justify-center text-center p-8" data-testid="kb-preview-fallback">
              <FileText className="h-10 w-10 text-slate-300 mb-3" />
              <p className="text-sm text-slate-600">{attachment.filename}</p>
              <p className="text-xs text-slate-400 mt-1">This file type cannot be previewed in the browser.</p>
              <a href={url} download={attachment.filename} className="mt-4">
                <Button size="sm"><Download className="h-4 w-4 mr-1" /> Download</Button>
              </a>
            </div>
          )}
        </div>
      </aside>
    </>,
    document.body
  );
};

export default AttachmentPreview;
